import {useState} from 'react';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {Button} from '@/components';
import {PermissionsProvider} from '@/components/common/PermissionsProvider';
import type {AfriexCountry} from '@/types';

import {formatKycProcessor} from './kycProcessorLabels';
import KycSettingsModal from './KycSettingsModal';

type KycSettingsProps = {
  handleSave: (id: string, body: Partial<AfriexCountry>) => Promise<void>;
  item: AfriexCountry;
};

/**
 * Settings -> Country -> KYC.
 *
 * The country record is the source of truth for this: it used to live in
 * `kycParams.primaryKYCProcessorByCountry` in config, which was never
 * Parameter-Store backed, so changing it needed a deploy.
 *
 * "Not set" is a real, safe state rather than a misconfiguration: the selector
 * falls through to the global default processor, which is exactly what an
 * unmapped country did before.
 */
const KycSettings = ({item, handleSave}: KycSettingsProps) => {
  const [showModal, setShowModal] = useState(false);
  const configured = item?.params?.kycProcessor;

  return (
    <>
      <div className="flex justify-end mb-6">
        <PermissionsProvider permission={AfriexAdminPermission.CountryUpdate}>
          <Button variant={'solid'} onClick={() => setShowModal(!showModal)}>
            Update KYC Processor
          </Button>
        </PermissionsProvider>
      </div>
      <div className="flex flex-col gap-3">
        <div className="overflow-x-auto">
          <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
            <p className="text-slate-500 dark:text-slate-100 text-sm">
              KYC Processor
            </p>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-100 w-1/2">
              {configured
                ? formatKycProcessor(configured)
                : 'Not set (uses global default)'}
            </p>
          </div>
        </div>
      </div>

      <KycSettingsModal
        onClose={() => setShowModal(!showModal)}
        showModal={showModal}
        handleSave={handleSave}
        item={item}
      />
    </>
  );
};

export default KycSettings;
